// Dependencies
import { createFileRoute } from "@tanstack/react-router";
import { LangLink as Link } from "@/components/LangLink";
import { Reveal } from "@/components/Reveal";
import { PageHero, PageShell } from "@/components/PageShell";
import { ImageGrid } from "@/components/ImageGrid";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Images
import heroImage from "@/assets/aerospace/hero.png";
import mission from "@/assets/mission.png";
import service1 from "@/assets/aerospace/service1.png";
import service2 from "@/assets/aerospace/service2.png";
import service3 from "@/assets/aerospace/service3.png";
import service4 from "@/assets/aerospace/service4.png";
import service5 from "@/assets/aerospace/service5.png";

export const Route = createFileRoute("/$lang/services/aerospace")({
  head: () => ({
    meta: [
      { title: i18n.t("services/aerospace:metaTitle", "Aerospace and Systems Engineering — BIZU Space") },
      {
        name: "description",
        content: i18n.t("services/aerospace:metaDesc",
          "Comprehensive systems engineering and aerospace engineering services."
        ),
      },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/services/aerospace" }],
  }),
  component: AerospaceService,
});

function AerospaceService() {
  const { t } = useTranslation("services/aerospace");

  return (
    <PageShell>
      {/* HERO */}
      <PageHero title={t("heroTitle", " Aerospace and Systems Engineering")} image={heroImage} />

      {/* CONTENT */}
      <section className="py-24">
        <div className="container-bizu">
          {/* SYSTEMS ENGINEERING */}
          <Reveal effect="up">
            <h2 className="text-4xl font-semibold leading-tight md:text-5xl">
              {t("systemsTitle", "Systems Engineering:")}
            </h2>

            <p className="mt-6 text-base leading-relaxed">
              {t("systemsDesc",
                "At BIZU Space, we specialize in providing comprehensive systems engineering services to support the development of complex aerospace projects. Our expertise includes:"
              )}
            </p>

            <ul className="mt-6 list-disc space-y-2 pl-6 text-base leading-relaxed">
              <li>{t("systemsBullets.b1", "Development Planning")}</li>
              <li>{t("systemsBullets.b2", "Procurement of High-Complexity Materials")}</li>
              <li>{t("systemsBullets.b3", "Documentation Adhering to International Standards")}</li>
              <li>{t("systemsBullets.b4", "Flight Safety Analyses")}</li>
              <li>{t("systemsBullets.b5", "Ground Safety Analyses")}</li>
              <li>{t("systemsBullets.b6", "Launch Operations Planning")}</li>
              <li>{t("systemsBullets.b7", "Launch Authorization Documentation")}</li>
            </ul>

            <blockquote className="mt-10 border-l-4 border-accent pl-6 text-lg italic leading-relaxed">
              {t("systemsQuote",
                "Our commitment to excellence ensures that every aspect of your project is meticulously planned and executed to meet the highest standards of safety and performance. We provide this service for the development and the launch operation of the "
              )}
              <Link to="/mlbr" className="underline hover:text-accent transition-colors">
                MLBR — Brazilian Microlauncher
              </Link>
              {t("systemsQuoteExtra", " launch vehicle.")}
            </blockquote>
          </Reveal>

          <ImageGrid layout="gallery" className="mt-14" images={[mission, service1, service2]} />

          <Reveal effect="up" className="mt-20">
            <h2 className="text-4xl font-semibold leading-tight md:text-5xl">
              {t("aerospaceTitle", "Aerospace Engineering:")}
            </h2>

            <p className="mt-6 text-base leading-relaxed">
              {t("aerospaceDesc",
                "We also offer a wide range of aerospace engineering services designed to meet the needs of the most demanding projects. Our services include:"
              )}
            </p>

            <ul className="mt-6 list-disc space-y-2 pl-6 text-base leading-relaxed">
              <li>{t("aerospaceBullets.b1", "Trajectory Optimization")}</li>
              <li>{t("aerospaceBullets.b2", "Flight Simulation")}</li>
              <li>{t("aerospaceBullets.b3", "Launch Mission Analysis")}</li>
              <li>{t("aerospaceBullets.b4", "Aerodynamics Analysis")}</li>
              <li>{t("aerospaceBullets.b5", "Propulsion Analysis")}</li>
              <li>
                {t("aerospaceBullets.b6",
                  "Composite Propellant Development – derived from our expertise with the "
                )}
                <Link
                  to="/about"
                  hash="bcp"
                  className="underline hover:text-accent transition-colors"
                >
                  Bizu Composite Propellant (BCP)
                </Link>
                {t("aerospaceBullets.b6Extra", " development")}
              </li>
            </ul>

            <p className="mt-6 text-base leading-relaxed">
              {t("aerospaceDescExtra",
                "In the Launch Mission Analysis we can provide simulations of launch vehicle performance from different launch centers, different orbit inclinations and trajectories."
              )}
            </p>

            <blockquote className="mt-10 border-l-4 border-accent pl-6 text-lg italic leading-relaxed">
              {t("aerospaceQuote",
                "Our team of experts is dedicated to delivering innovative solutions and ensuring the success of your aerospace endeavors through meticulous analysis and cutting-edge technology. We are currently working on the "
              )}
              <Link to="/mlbr" className="underline hover:text-accent transition-colors">
                MLBR
              </Link>
              {t("aerospaceQuoteExtra",
                " development providing this kind of service."
              )}
            </blockquote>
          </Reveal>

          <ImageGrid className="mt-14" columns={3} images={[service3, service4, service5]} />
        </div>
      </section>
    </PageShell>
  );
}
